<script setup lang="ts">
import type { TSectionCategoryFormOption } from "@/app/tools/new-section-management/section-form/section-form.types";

import { cn, getDifferences, isDefined, walkTreeByLevel } from "@voltalis/frontend-utils";
import { BaseDrawer, IVirtualizedTreeNode, SectionAppliancesSummary } from "@voltalis/ui-kit/components";
import { SectionPath } from "@voltalis/ui-kit/components";
import { isEmpty } from "es-toolkit/compat";
import { computed, useTemplateRef, watch } from "vue";
import { ref } from "vue";

import { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import { useUpdateSectionMutation } from "@/composables/kili/sections/mutations/use-update-section-mutation";
import { useUpdateSectionsCategoriesMutation } from "@/composables/kili/sections/mutations/use-update-sections-categories-mutation";
import { useSiteSectionCategory } from "@/composables/kili/sections/use-site-section-category";
import { useToaster } from "@/composables/layout/use-toaster";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { EV4bErrorCode } from "@/data/api/kili/common/kili-errors.type";
import { TSiteDetails } from "@/data/api/kili/sites/types/sites.type";
import { SiteSectionId, V4HSiteId } from "@/data/common/ids";
import { SectionNode } from "@/domains/v4b/site-section/site-section.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { getV4bErrorCode } from "@/utils/errors/v4b-errors-utils";

import Divider from "primevue/divider";

import SectionForm from "@/app/tools/new-section-management/section-form/section-form.vue";
import SectionAppliancesV2Dialog from "@/app/tools/new-section-management/site-sections-tree/section-appliances/section-appliances-v2-dialog.vue";
import DeleteSectionCta from "@/app/tools/new-section-management/site-sections-tree/section-details/delete-section-cta.vue";
import SectionDetailsReadonlyContent from "@/components/tools/site-section/section-details-readonly-content.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import ConfirmationDialog from "@/components/ui/dialog/confirmation-dialog.vue";

interface IProps {
  siteId: V4HSiteId;
  section?: SectionNode;
  isLoading?: boolean;
  siteSectionsTree?: IVirtualizedTreeNode<TSiteSectionsTreeData>;
  sectionParent?: TSiteSectionsTreeData;
  sectionCategoryOptions?: readonly TSectionCategoryFormOption[];
  siteDetails?: TSiteDetails;
}

interface IEmits {
  (e: "hide"): void;
  (e: "delete-section", section: SectionNode): void;
  (e: "pending-move-preview-change", preview: { section: SectionNode; parent: TSiteSectionsTreeData } | null): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const visible = defineModel<boolean>({
  required: true,
});

const { t, k } = useTypedIntl();
const toast = useToaster();

const drawer = useTemplateRef("drawer");
const editForm = useTemplateRef("editForm");

const splittedParentsNamePath = computed<Array<string>>(() => {
  if (!props.section?.parent) return [];

  return props.section.parent.namePath.split("/");
});

const maybeParentName = computed<string | undefined>(() => {
  if (!splittedParentsNamePath.value.length) return;

  return splittedParentsNamePath.value[splittedParentsNamePath.value.length - 1];
});

const computedSiteId = computed(() => props.siteId);
const computedSectionId = computed(() => props.section?.id);
const { updateSectionDetails, isPending: isUpdatingSectionDetails } = useUpdateSectionMutation(
  computedSiteId,
  computedSectionId,
  {
    onError: (error) => {
      if (getV4bErrorCode(error) === EV4bErrorCode.SITE_SECTION_ALREADY_EXISTS_WITH_SAME_NAME) {
        editForm.value?.setSiteSectionsFieldsetCustomError("name", "section_name_already_used");
      }
    },
  },
);
const { mutateAsync: updateSectionsCategories, isPending: isUpdatingSectionsCategories } =
  useUpdateSectionsCategoriesMutation(computedSiteId);
const { createSiteSectionCategory, isCreatingSectionCategory } = useSiteSectionCategory(computedSiteId);
const isUpdatingSection = computed<boolean>(
  () => isUpdatingSectionDetails.value || isUpdatingSectionsCategories.value || isCreatingSectionCategory.value,
);

const isSectionAppliancesDialogVisible = ref<boolean>(false);
const isEditing = ref<boolean>(false);
const isFormDirty = ref<boolean>(false);
const isQuitEditionConfirmationDialogVisible = ref<boolean>(false);
const sectionChildrenIds = computed<SiteSectionId[]>(() => {
  const result: SiteSectionId[] = [];

  for (const section of walkTreeByLevel(props.section?.children)) {
    result.push(section.id);
  }

  return result;
});

function resetEdition() {
  isEditing.value = false;
  isFormDirty.value = false;
  emits("pending-move-preview-change", null);
}

function beforeCloseDrawer() {
  if (!isEditing.value) return true;

  if (!isFormDirty.value) {
    resetEdition();
    return false;
  }

  isQuitEditionConfirmationDialogVisible.value = true;
  return false;
}

function handleConfirmQuitEdition() {
  resetEdition();
  isQuitEditionConfirmationDialogVisible.value = false;
}

async function submit() {
  if (!isEditing.value) return;

  const payload = await editForm.value?.submit();
  if (!payload) return;
  if (!props.section) return;

  const diffs = getDifferences(payload.initialValues, payload.newValues);
  const initialCategoryId = payload.initialValues.categoryId ?? null;
  const newCategoryId = payload.newValues.categoryId ?? null;
  const sectionId = props.section.id;
  const categoryUpdateRequest =
    initialCategoryId === newCategoryId
      ? undefined
      : {
          sectionsWithCategories: [
            {
              sectionId,
              sectionCategoryId: newCategoryId,
            },
          ],
        };

  const patchRequest = isEmpty(diffs)
    ? undefined
    : {
        area: isDefined(diffs.area) ? diffs.area : undefined,
        name: diffs.name,
      };

  const moveRequest =
    diffs.parent === undefined
      ? undefined
      : {
          parentId: diffs.parent?.id ?? null,
        };

  let wasUpdated = await updateSectionDetails({
    patchRequest,
    moveRequest,
  });

  if (wasUpdated && categoryUpdateRequest) {
    await updateSectionsCategories(categoryUpdateRequest)
      .then(() => {
        MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_ASSIGNED_TO_SECTION, {
          action_type: "single",
          action: newCategoryId ? "assigned" : "removed",
          space_id: sectionId,
          category_id: newCategoryId ?? undefined,
        });
      })
      .catch((err) => {
        console.error(err);
        wasUpdated = false;
      });
  }

  if (wasUpdated) {
    toast.add({
      severity: "success",
      detail: t(k.tools.siteManagement.sectionDetails.edition.message.success),
    });

    if (patchRequest) {
      if (patchRequest.name) {
        MixpanelService.trackEvent(EMixpanelEvent.SECTION_RENAMED, {
          space_id: sectionId,
        });
      }

      if (isDefined(patchRequest.area)) {
        MixpanelService.trackEvent(EMixpanelEvent.SECTION_AREA_UPDATED, {
          space_id: sectionId,
        });
      }
    }

    if (moveRequest) {
      MixpanelService.trackEvent(EMixpanelEvent.SECTION_MOVED, {
        method: "form",
      });
    }

    resetEdition();
  } else {
    toast.add({
      severity: "error",
      detail: t(k.tools.siteManagement.sectionDetails.edition.message.error),
    });
  }
}

function handleDrawerHide() {
  resetEdition();
  emits("hide");
}

function isNodeDisabled(node: IVirtualizedTreeNode<TSiteSectionsTreeData>) {
  const isNodeChildOfCurrentSection: boolean = node.data ? sectionChildrenIds.value.includes(node.data.id) : false;
  return node.data?.id === props.section?.id || isNodeChildOfCurrentSection;
}

function getParentId(parent: TSiteSectionsTreeData | undefined): SiteSectionId | null {
  return parent?.id ?? null;
}

function handlePendingMovePreviewChange(parent: TSiteSectionsTreeData) {
  if (!props.section) return;

  if (getParentId(parent) === getParentId(props.sectionParent)) {
    emits("pending-move-preview-change", null);
    return;
  }

  emits("pending-move-preview-change", {
    section: props.section,
    parent,
  });
}

watch(
  () => props.section?.id,
  () => resetEdition(),
  { immediate: true },
);

watch(visible, (newValue) => {
  if (!newValue) return;

  MixpanelService.trackEvent(EMixpanelEvent.SECTION_DETAILS_VIEWED, {
    space_id: computedSectionId.value,
  });
});
</script>

<template>
  <BaseDrawer
    ref="drawer"
    v-model="visible"
    :before-close="beforeCloseDrawer"
    :data-test-id="DATA_TEST_IDS.components.sectionDetailsDrawer.component"
    :header-class="isEditing ? 'bg-blue-50 !border-b-0 rounded-t-lg' : undefined"
    @hide="handleDrawerHide"
  >
    <template #title>
      <div
        v-if="isEditing"
        class="flex items-center gap-2 text-brand"
      >
        <i class="pi pi-pencil text-sm" />
        <div class="flex flex-col gap-0">
          <span class="font-semibold leading-tight">
            {{ t(k.tools.siteManagement.sectionDetails.edition.editionMode) }}
          </span>
          <span class="text-sm leading-tight">
            {{ t(k.tools.siteManagement.sectionDetails.edition.changesNotSaved) }}
          </span>
        </div>
      </div>
      <div
        v-else
        class="flex flex-col gap-2"
      >
        <h4
          class="font-semibold text-gray-700"
          :data-test-id="DATA_TEST_IDS.components.sectionDetailsDrawer.sectionName"
        >
          {{ props.section?.name }}
        </h4>
        <SectionPath
          v-if="splittedParentsNamePath.length"
          :section-parents-path="splittedParentsNamePath"
          :section-name="section?.name"
        />
      </div>
    </template>

    <div class="flex flex-col justify-between h-full overflow-auto">
      <div class="flex flex-col gap-2 pt-8 overflow-y-auto">
        <div class="flex flex-col gap-6 px-6">
          <div class="flex justify-between items-center">
            <small class="uppercase text-gray-600">
              {{ t(k.tools.siteManagement.sectionDetails.informations) }}
            </small>

            <button
              v-if="!isEditing"
              class="text-brand flex items-center gap-2"
              :data-test-id="DATA_TEST_IDS.components.sectionDetailsDrawer.editButton"
              @click.stop="isEditing = true"
            >
              <i class="pi pi-pencil" />
              <span class="font-bold">{{ t(k.global.misc.edit) }}</span>
            </button>
          </div>

          <SectionForm
            v-if="isEditing"
            v-model:dirty="isFormDirty"
            ref="editForm"
            :initial-section="props.section"
            :is-loading="isUpdatingSection"
            :isParentSelectionNodeDisabled="isNodeDisabled"
            :initialSelectedParent="props.sectionParent"
            :maybe-initial-section-parent="props.sectionParent"
            :category-options="props.sectionCategoryOptions"
            :create-category="createSiteSectionCategory"
            :is-creating-category="isCreatingSectionCategory"
            :site-details="props.siteDetails"
            :site-sections-tree="props.siteSectionsTree"
            :target-site-root-translation-key="k.tools.siteManagement.sectionDetails.edition.targetRoot"
            @select-parent="handlePendingMovePreviewChange"
            @submit="submit"
          />

          <SectionDetailsReadonlyContent
            v-else
            :parent-name="maybeParentName"
            :section="props.section"
          />
        </div>

        <Divider />

        <div :class="cn('flex flex-col gap-6 px-6', { 'opacity-[0.35] pointer-events-none': isEditing })">
          <SectionAppliancesSummary
            :appliance-count-by-programming-category="props.section?.applianceCountByCategory"
            isMoveAppliancesEnabled
            @move-appliances="isSectionAppliancesDialogVisible = true"
            @see-all-appliances="isSectionAppliancesDialogVisible = true"
          />
        </div>
        <Divider />
        <DeleteSectionCta
          :section="props.section"
          :siteId="props.siteId"
          @deletion-completed="drawer?.handleClose()"
        />
      </div>

      <div class="flex flex-col gap-3 w-full px-6 pb-6">
        <HospitalityButton
          :data-test-id="DATA_TEST_IDS.components.sectionDetailsDrawer.saveButton"
          :disabled="!isEditing || !isFormDirty"
          :label="t(k.global.misc.save)"
          :loading="props.isLoading || isUpdatingSection"
          size="sm"
          @click="submit"
        />
        <HospitalityButton
          :data-test-id="DATA_TEST_IDS.components.sectionDetailsDrawer.closeButton"
          :disabled="props.isLoading || isUpdatingSection"
          :label="isEditing ? t(k.global.misc.cancel) : t(k.global.misc.close)"
          severity="secondary"
          size="sm"
          @click="drawer?.handleClose()"
        />
      </div>
    </div>
  </BaseDrawer>

  <SectionAppliancesV2Dialog
    v-if="props.section"
    v-model="isSectionAppliancesDialogVisible"
    :siteId="props.siteId"
    :section="props.section"
    :site-sections-tree="props.siteSectionsTree"
  />

  <ConfirmationDialog
    v-model="isQuitEditionConfirmationDialogVisible"
    delete-mode
    :callback="handleConfirmQuitEdition"
    :cancel-text="t(k.tools.siteManagement.sectionDetails.edition.abandonChanges.continueEdition)"
    :title="t(k.tools.siteManagement.sectionDetails.edition.abandonChanges.title)"
    :subtitle="t(k.tools.siteManagement.sectionDetails.edition.abandonChanges.message)"
    :submit-text="t(k.tools.siteManagement.sectionDetails.edition.abandonChanges.abandon)"
  />
</template>

<style lang="scss">
.section-details-drawer {
  .p-sidebar-header {
    border-bottom: 1px solid var(--color-neutral-light);
  }

  .p-sidebar-content {
    padding: 0;
  }
}
</style>
